import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { TrendingUp, ArrowLeft, BarChart3, Target, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const ProgressPage = () => {
  const navigate = useNavigate();
  const [progressData, setProgressData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProgress();
  }, []);

  const fetchProgress = async () => {
    try {
      const response = await apiService.getProgress();
      setProgressData(response.data);
    } catch (error) {
      toast.error('Failed to load progress data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F8FB] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#4D96FF] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg font-semibold text-[#64748B]">Loading progress...</p>
        </div>
      </div>
    );
  }

  const overallStats = progressData?.overall_stats || {};
  const subjectStats = progressData?.subject_stats || [];
  const timeline = progressData?.progress_timeline || [];

  return (
    <div className="min-h-screen bg-[#F4F8FB]" data-testid="progress-page">
      {/* Header */}
      <div className="bg-white border-b-2 border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button
            data-testid="back-to-dashboard-btn"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-[#64748B] hover:text-[#1E293B] font-semibold mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Dashboard
          </button>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-[#4D96FF] to-[#A493FF] rounded-3xl flex items-center justify-center">
              <TrendingUp className="w-10 h-10 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-black text-[#1E293B]">Progress Report</h1>
              <p className="text-lg text-[#64748B]">Track your growth over time</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overall Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <StatCard
            label="Elo Rating"
            value={Math.round(overallStats.elo_rating || 1500)}
            icon={<Target className="w-6 h-6" />}
            bgColor="bg-[#D8E8FF]"
            textColor="text-[#4D96FF]"
          />
          <StatCard
            label="Total Questions"
            value={overallStats.total_questions || 0}
            icon={<BarChart3 className="w-6 h-6" />}
            bgColor="bg-[#FFF3D6]"
            textColor="text-[#FFC03D]"
          />
          <StatCard
            label="Accuracy"
            value={`${overallStats.accuracy || 0}%`}
            icon={<TrendingUp className="w-6 h-6" />}
            bgColor="bg-[#E8F5E9]"
            textColor="text-[#38E54D]"
          />
          <StatCard
            label="Current Streak"
            value={`${overallStats.current_streak || 0} days`}
            icon={<Calendar className="w-6 h-6" />}
            bgColor="bg-[#FFE8D6]"
            textColor="text-orange-500"
          />
        </div>

        {/* Elo Progress Chart */}
        {timeline.length > 0 && (
          <div className="bg-white rounded-3xl p-8 border-2 border-slate-100 shadow-xl mb-8" data-testid="progress-chart">
            <h2 className="text-2xl font-black text-[#1E293B] mb-6 flex items-center gap-3">
              <TrendingUp className="w-7 h-7 text-[#4D96FF]" />
              Your Elo Rating Over Time
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={timeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis 
                  dataKey="date" 
                  stroke="#64748B"
                  style={{ fontSize: '12px', fontWeight: '600' }}
                />
                <YAxis 
                  stroke="#64748B"
                  style={{ fontSize: '12px', fontWeight: '600' }}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '2px solid #E2E8F0',
                    borderRadius: '12px',
                    padding: '12px'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="elo_rating" 
                  stroke="#4D96FF" 
                  strokeWidth={3}
                  dot={{ fill: '#4D96FF', r: 5 }}
                  activeDot={{ r: 7 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Subject Breakdown */}
        <div className="bg-white rounded-3xl p-8 border-2 border-slate-100 shadow-xl">
          <h2 className="text-2xl font-black text-[#1E293B] mb-6">Subject Performance</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {subjectStats.map((stat, idx) => (
              <SubjectProgressCard key={idx} stat={stat} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, icon, bgColor, textColor }) => (
  <div className="bg-white rounded-2xl p-6 border-2 border-slate-100 shadow-lg">
    <div className={`w-12 h-12 ${bgColor} rounded-2xl flex items-center justify-center ${textColor} mb-3`}>
      {icon}
    </div>
    <p className="text-sm font-semibold text-[#64748B] mb-1">{label}</p>
    <p className="text-3xl font-black text-[#1E293B]">{value}</p>
  </div>
);

const SubjectProgressCard = ({ stat }) => {
  const accuracy = stat.questions_answered > 0 
    ? ((stat.correct_answers / stat.questions_answered) * 100).toFixed(1)
    : 0;

  const getSubjectColor = (subject) => {
    const colors = {
      mathematics: { bg: 'bg-blue-100', text: 'text-blue-600', gradient: 'from-blue-400 to-blue-600' },
      science: { bg: 'bg-green-100', text: 'text-green-600', gradient: 'from-green-400 to-green-600' },
      english: { bg: 'bg-purple-100', text: 'text-purple-600', gradient: 'from-purple-400 to-purple-600' }
    };
    return colors[subject] || colors.mathematics;
  };

  const colors = getSubjectColor(stat.subject);

  return (
    <div className={`bg-gradient-to-br ${colors.gradient} rounded-2xl p-6 text-white`}>
      <h3 className="text-2xl font-black mb-4 capitalize">{stat.subject}</h3>
      <div className="space-y-3">
        <div>
          <p className="text-sm opacity-90 font-semibold mb-1">Elo Rating</p>
          <p className="text-3xl font-black">{Math.round(stat.elo_rating)}</p>
        </div>
        <div>
          <p className="text-sm opacity-90 font-semibold mb-1">Accuracy</p>
          <div className="flex items-center gap-3">
            <div className="flex-1 bg-white/30 rounded-full h-3">
              <div 
                className="bg-white h-3 rounded-full transition-all duration-300"
                style={{ width: `${accuracy}%` }}
              ></div>
            </div>
            <p className="text-xl font-bold">{accuracy}%</p>
          </div>
        </div>
        <div className="flex justify-between text-sm pt-2 border-t border-white/30">
          <div>
            <p className="opacity-90">Questions</p>
            <p className="font-bold">{stat.questions_answered}</p>
          </div>
          <div>
            <p className="opacity-90">Correct</p>
            <p className="font-bold">{stat.correct_answers}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressPage;